
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Specialty {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export const useSpecialties = (includeInactive = false) => {
  return useQuery({
    queryKey: ['specialties', includeInactive],
    queryFn: async () => {
      let query = supabase
        .from('specialties')
        .select('*')
        .order('display_order', { ascending: true });

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching specialties:', error);
        throw error;
      }

      return data as Specialty[];
    },
  });
};

export const useCreateSpecialty = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (specialty: Omit<Specialty, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('specialties')
        .insert(specialty)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialties'] });
      toast({
        title: "Success",
        description: "Specialty created successfully.",
      });
    },
    onError: (error) => {
      console.error('Error creating specialty:', error);
      toast({
        title: "Error",
        description: "Failed to create specialty.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateSpecialty = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Specialty> & { id: string }) => {
      const { data, error } = await supabase
        .from('specialties')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialties'] });
      toast({
        title: "Success",
        description: "Specialty updated successfully.",
      });
    },
    onError: (error) => {
      console.error('Error updating specialty:', error);
      toast({
        title: "Error",
        description: "Failed to update specialty.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteSpecialty = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('specialties')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['specialties'] });
      toast({
        title: "Success",
        description: "Specialty deleted successfully.",
      });
    },
    onError: (error) => {
      console.error('Error deleting specialty:', error);
      toast({
        title: "Error",
        description: "Failed to delete specialty.",
        variant: "destructive",
      });
    },
  });
};
